import { events } from "@constants/tracking";
import useCurrentSession from "@features/session/hooks/useCurrentSession";
import useSessionStore from "@features/session/hooks/useSessionStore";
import useAnalytics from "@hooks/useAnalytics";
import { useAppStore } from "@hooks/useAppStore";
import type { SessionItemsNavFilterValue } from "@memorang/ui/components/SessionItemsNavPane";
import { useDevice } from "@memorang/ui/useDevice";
import { router, useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useMemo } from "react";
import {
	buildSessionItemsNavItems,
	getSessionItemsNavFiltersForMode,
	getSessionItemsNavProgress,
} from "../containers/StudySessionBottombarContainer/session-items-nav";
import useEnqueueAssessmentOnLeave from "./useEnqueueAssessmentOnLeave";

type StateSlice = {
	setShowSessionItemsNav: React.Dispatch<React.SetStateAction<boolean>>;
	sessionItemsNavSelectedFilters: SessionItemsNavFilterValue[];
	setSessionItemsNavSelectedFilters: React.Dispatch<
		React.SetStateAction<SessionItemsNavFilterValue[]>
	>;
};

export const useSessionItemsNavCore = ({
	setShowSessionItemsNav,
	sessionItemsNavSelectedFilters,
	setSessionItemsNavSelectedFilters,
}: StateSlice) => {
	const id = useSessionStore((s) => s.id);
	const inReviewMode = useSessionStore((s) => s.inReviewMode);
	const isSummativeTest = useSessionStore((s) => s.isSummativeTest);
	const itemStates = useSessionStore((s) => s.itemStates);
	const answers = useSessionStore((s) => s.answers);
	const itemViewedTimestamps = useSessionStore((s) => s.itemViewedTimestamps);
	const blockStates = useSessionStore((s) => s.blockStates);
	const updateProgress = useSessionStore((s) => s.updateProgress);
	const allowMarking = useAppStore((s) => s.sessionView?.allowMarking ?? true);

	const { index } = useLocalSearchParams<{ index: string }>();
	const currentItemIndex = index ? Number.parseInt(index) - 1 : 0;

	const { explicitCallTrackCustomEvent } = useAnalytics();
	const { isDesktopWeb } = useDevice();
	const { currentBlock, currentItem, currentBlockIndex } = useCurrentSession();
	const currentItemId = currentItem?.id!;

	const enqueueAssessmentOnLeave = useEnqueueAssessmentOnLeave();

	const currentBlockState = blockStates.get(currentBlockIndex);
	const markedItems = currentBlockState?.markedItemsForReview;

	const sessionItemsNavItems = useMemo(
		() =>
			buildSessionItemsNavItems({
				items: currentBlock,
				itemStates,
				answers,
				markedItemIds: markedItems,
				inReviewMode,
				itemViewedTimestamps,
				allowMarking,
			}),
		[
			answers,
			currentBlock,
			inReviewMode,
			itemStates,
			itemViewedTimestamps,
			markedItems,
			allowMarking,
		],
	);

	const sessionItemsNavFilters = useMemo(
		() => getSessionItemsNavFiltersForMode(inReviewMode, allowMarking),
		[inReviewMode, allowMarking],
	);

	const handleCloseSessionItemsNav = useCallback(() => {
		setShowSessionItemsNav(false);
	}, [setShowSessionItemsNav]);

	const handleOpenSessionItemsNav = useCallback(() => {
		if (!isSummativeTest) {
			return;
		}
		explicitCallTrackCustomEvent({
			eventName: events.sessionItemsNavClicked,
			sessionId: id,
			itemId: currentItemId,
			inReviewMode: String(inReviewMode),
		});
		setShowSessionItemsNav(true);
	}, [
		currentItemId,
		explicitCallTrackCustomEvent,
		id,
		inReviewMode,
		isSummativeTest,
		setShowSessionItemsNav,
	]);

	const handleSessionItemsNavPress = useCallback(
		(item: (typeof sessionItemsNavItems)[number], itemIndex: number) => {
			if (item.disabled) {
				return;
			}
			explicitCallTrackCustomEvent({
				eventName: events.sessionItemNavigated,
				sessionId: id,
				itemId: currentItemId,
				type: "jump",
				targetIndex: itemIndex + 1,
			});
			if (!inReviewMode) {
				enqueueAssessmentOnLeave(
					currentItemId,
					isSummativeTest,
					currentItem.metadata?.type,
				);
			}
			updateProgress(
				getSessionItemsNavProgress(itemIndex, currentBlock.length),
			);
			if (!isDesktopWeb) {
				setShowSessionItemsNav(false);
			}
			router.setParams({
				index: String(itemIndex + 1),
				prev: itemIndex < currentItemIndex ? "true" : "false",
			});
		},
		[
			currentBlock.length,
			currentItem,
			currentItemId,
			currentItemIndex,
			enqueueAssessmentOnLeave,
			explicitCallTrackCustomEvent,
			id,
			inReviewMode,
			isDesktopWeb,
			isSummativeTest,
			setShowSessionItemsNav,
			updateProgress,
		],
	);

	useEffect(() => {
		if (!isSummativeTest) {
			setShowSessionItemsNav(false);
			setSessionItemsNavSelectedFilters([]);
		}
	}, [
		isSummativeTest,
		setSessionItemsNavSelectedFilters,
		setShowSessionItemsNav,
	]);

	return {
		isSummativeTest,
		sessionItemsNavItems,
		sessionItemsNavFilters,
		handleOpenSessionItemsNav,
		handleCloseSessionItemsNav,
		handleSessionItemsNavPress,
		questionsNavActiveFilterCount: sessionItemsNavSelectedFilters.length,
	};
};
